import { LitElement, html, css, unsafeCSS } from 'lit';
import QRCode from 'qrcode';
import { auth } from '../auth.js';
import { api } from '../api.js';
import wordmarkUrl from '../assets/logo-text.png';
import loginBg from '../assets/login-bg.jpg';
import styles from '../styles/login-form.css?inline';

// Inline stroke icons (kept local so the login screen has no icon-lib dependency).
const IC = {
  mail: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2.5"/><path d="m3.5 7 8.5 6 8.5-6"/></svg>`,
  lock: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><rect x="4.5" y="10.5" width="15" height="9.5" rx="2.5"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/></svg>`,
  eye: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7Z"/><circle cx="12" cy="12" r="3"/></svg>`,
  eyeOff: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="m3 3 18 18"/><path d="M10.6 10.6a3 3 0 0 0 4.2 4.2"/><path d="M9.9 4.24A10.9 10.9 0 0 1 12 4c6.4 0 10 7 10 7a17.8 17.8 0 0 1-3.15 4.17M6.6 6.6A17.8 17.8 0 0 0 2 12s3.6 7 10 7a10.9 10.9 0 0 0 3.4-.55"/></svg>`,
  arrow: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12h15"/><path d="m13 6 6 6-6 6"/></svg>`,
  leaf: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20.5A7.5 7.5 0 0 1 3.5 13C3.5 7 9 3.5 20.5 3.5c0 8.5-4.2 14-9.5 14Z"/><path d="M8.5 16.5c1-3.2 3.2-5.4 6.5-6.5"/></svg>`,
  shield: html`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="5" y="10.5" width="14" height="9" rx="2"/><path d="M8 10.5V8a4 4 0 0 1 8 0v2.5"/></svg>`,
};

export class LoginForm extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;

  static properties = {
    email: { type: String },
    password: { type: String },
    loading: { type: Boolean },
    error: { type: String },
    // step: 'login' | 'mfa' | 'setup' | 'forgot'
    mode: { type: String },
    code: { type: String },
    qrDataUrl: { type: String },
    secret: { type: String },
    recoveryMode: { type: Boolean },
    recoverySent: { type: Boolean },
    info: { type: String },
    forgotSent: { type: Boolean },
    _showPassword: { type: Boolean, state: true },
  };

  constructor() {
    super();
    this.email = '';
    this.password = '';
    this.loading = false;
    this.error = null;
    this.mode = 'login';
    this.code = '';
    this.qrDataUrl = null;
    this.secret = '';
    this.recoveryMode = false;
    this.recoverySent = false;
    this.info = null;
    this.forgotSent = false;
    this._showPassword = false;
    this._mfaToken = null;
    this._setupToken = null;
  }

  async _submitForgot(e) {
    e?.preventDefault();
    if (!this.email.trim()) { this.error = 'Vul je e-mailadres in.'; return; }
    this.error = null; this.loading = true;
    try {
      await api.forgotPassword(this.email.trim());
    } catch { /* always generic — ignore errors to avoid leaking anything */ }
    this.loading = false;
    this.forgotSent = true;
  }

  /** Route a login/verify result: success → app, or advance to the right 2FA step. */
  _handleResult(result) {
    if (result.success) { this.dispatchEvent(new CustomEvent('login-success')); return true; }
    if (result.requiresMfa) { this._mfaToken = result.mfaToken; this.mode = 'mfa'; this.code = ''; this.error = null; this.loading = false; return true; }
    if (result.requiresMfaSetup) { this._beginSetup(result.setupToken); return true; }
    return false;
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.error = null;
    this.loading = true;
    const result = await auth.login(this.email, this.password);
    if (this._handleResult(result)) return;
    this.error = result.error;
    this.loading = false;
  }

  async _submitMfa(e) {
    e?.preventDefault();
    if (!/^\d{6}$/.test(this.code.trim())) { this.error = 'Voer de 6-cijferige code in.'; return; }
    this.error = null; this.loading = true;
    const result = await auth.verifyMfa(this._mfaToken, this.code.trim());
    if (this._handleResult(result)) return;
    this.error = result.error || 'Ongeldige code';
    this.loading = false;
  }

  async _beginSetup(setupToken) {
    this._setupToken = setupToken;
    this.mode = 'setup'; this.code = ''; this.error = null; this.recoveryMode = false;
    this.loading = true;
    try {
      const { otpauthUri, secret } = await auth.mfaSetupBegin(setupToken);
      this.secret = secret;
      this.qrDataUrl = await QRCode.toDataURL(otpauthUri, { width: 190, margin: 1 });
    } catch (err) {
      this.error = err.message || 'Kon 2FA-installatie niet starten';
    } finally {
      this.loading = false;
    }
  }

  async _submitSetup(e) {
    e?.preventDefault();
    if (!/^\d{6}$/.test(this.code.trim())) { this.error = 'Voer de 6-cijferige code in.'; return; }
    this.error = null; this.loading = true;
    const result = await auth.mfaSetupEnable(this._setupToken, this.code.trim());
    if (result.success) { this.dispatchEvent(new CustomEvent('login-success')); return; }
    this.error = result.error || 'Ongeldige code';
    this.loading = false;
  }

  async _startRecovery() {
    this.error = null; this.loading = true;
    const res = await auth.recoverMfaStart(this._mfaToken);
    this.loading = false;
    if (res.success) { this.recoveryMode = true; this.recoverySent = true; this.code = ''; this.info = 'We hebben een herstelcode naar je e-mailadres gestuurd.'; }
    else { this.error = res.error || 'Kon geen herstelcode versturen'; }
  }

  async _submitRecovery(e) {
    e?.preventDefault();
    this.error = null; this.loading = true;
    const result = await auth.recoverMfaVerify(this._mfaToken, this.code.trim());
    if (result.requiresMfaSetup) { this.recoveryMode = false; this.info = null; this._beginSetup(result.setupToken); return; }
    if (result.success) { this.dispatchEvent(new CustomEvent('login-success')); return; }
    this.error = result.error || 'Ongeldige herstelcode';
    this.loading = false;
  }

  _codeInput(onSubmit, placeholder = '000000') {
    return html`
      <input
        id="code" type="text" inputmode="numeric" autocomplete="one-time-code"
        maxlength=${placeholder.length} placeholder=${placeholder}
        style="letter-spacing:6px; text-align:center; font-size:1.2rem;"
        .value=${this.code}
        @input=${(e) => { this.code = e.target.value.replace(/\D/g, ''); this.error = null; }}
        @keydown=${(e) => { if (e.key === 'Enter') onSubmit(e); }}
        ?disabled=${this.loading}
        autofocus
      />`;
  }

  _renderBody() {
    // --- Recovery code entry (lost authenticator) ---
    if (this.mode === 'mfa' && this.recoveryMode) {
      return html`
        <h2>Herstelcode</h2>
        <p class="subtitle">${this.info || 'Voer de 8-cijferige code uit je e-mail in.'}</p>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <form @submit=${this._submitRecovery}>
          <div class="form-group"><label for="code">Herstelcode</label>${this._codeInput((e) => this._submitRecovery(e), '00000000')}</div>
          <button type="submit" ?disabled=${this.loading}>${this.loading ? 'Bezig…' : 'Bevestigen'}</button>
        </form>
        <button class="linklike" @click=${() => { this.recoveryMode = false; this.code = ''; this.error = null; this.info = null; }} style="margin-top:14px; background:none; border:none; color:var(--mv-primary,#10B981); cursor:pointer; font-size:0.85rem;">← Terug naar code invoeren</button>
      `;
    }

    // --- TOTP code entry (2FA login step) ---
    if (this.mode === 'mfa') {
      return html`
        <h2>Verificatie</h2>
        <p class="subtitle">Voer de code uit je authenticator-app in.</p>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <form @submit=${this._submitMfa}>
          <div class="form-group"><label for="code">6-cijferige code</label>${this._codeInput((e) => this._submitMfa(e))}</div>
          <button type="submit" ?disabled=${this.loading}>${this.loading ? 'Bezig…' : 'Verifiëren'}</button>
        </form>
        <button class="linklike" @click=${() => this._startRecovery()} ?disabled=${this.loading} style="margin-top:14px; background:none; border:none; color:var(--mv-primary,#10B981); cursor:pointer; font-size:0.85rem;">Authenticator kwijt? Mail me een herstelcode</button>
      `;
    }

    // --- Forced enrollment (sysadmin without 2FA) ---
    if (this.mode === 'setup') {
      return html`
        <h2>Stel 2FA in</h2>
        <p class="subtitle">Tweestapsverificatie is verplicht voor systeembeheerders. Scan de QR-code en voer een code in.</p>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <div style="display:flex; flex-direction:column; align-items:center; gap:12px; margin-bottom:8px;">
          ${this.qrDataUrl ? html`<img src=${this.qrDataUrl} alt="QR-code" style="width:170px; height:170px; border:1px solid #e5e7eb; border-radius:10px;" />` : ''}
          ${this.secret ? html`<div style="font-family:monospace; font-size:0.78rem; word-break:break-all; text-align:center; color:#6b7280;">${this.secret}</div>` : ''}
        </div>
        <form @submit=${this._submitSetup}>
          <div class="form-group"><label for="code">6-cijferige code</label>${this._codeInput((e) => this._submitSetup(e))}</div>
          <button type="submit" ?disabled=${this.loading}>${this.loading ? 'Bezig…' : 'Inschakelen & inloggen'}</button>
        </form>
      `;
    }

    // --- Forgot password ---
    if (this.mode === 'forgot') {
      if (this.forgotSent) {
        return html`
          <h2>Controleer je e-mail</h2>
          <p class="subtitle">Als er een account bij dit e-mailadres hoort, hebben we een link gestuurd om je wachtwoord opnieuw in te stellen. De link is 1 uur geldig.</p>
          <button type="button" class="btn-primary" @click=${() => { this.mode = 'login'; this.forgotSent = false; this.error = null; }}>Terug naar inloggen</button>
        `;
      }
      return html`
        <h2>Wachtwoord vergeten</h2>
        <p class="subtitle">Vul je e-mailadres in; we sturen je een link om een nieuw wachtwoord te kiezen.</p>
        ${this.error ? html`<div class="error">${this.error}</div>` : ''}
        <form @submit=${this._submitForgot}>
          <div class="form-group">
            <label for="email">E-mailadres</label>
            <input id="email" type="email" name="email" autocomplete="username" placeholder="name@company.com"
              .value=${this.email} @input=${(e) => this.email = e.target.value} required ?disabled=${this.loading} />
          </div>
          <button type="submit" ?disabled=${this.loading}>${this.loading ? 'Bezig…' : 'Stuur resetlink'}</button>
        </form>
        <button class="linklike" @click=${() => { this.mode = 'login'; this.error = null; }} style="margin-top:14px; background:none; border:none; color:var(--mv-text-sub,#6b7280); cursor:pointer; font-size:0.85rem;">← Terug naar inloggen</button>
      `;
    }

    // --- Normal login ---
    return html`
      <h2>Welkom terug</h2>
      <p class="subtitle">Log in om toegang te krijgen tot je projecten en data.</p>
      ${this.error ? html`<div class="error">${this.error}</div>` : ''}
      <form @submit=${this.handleSubmit}>
        <div class="field">
          <label for="email">E-mailadres</label>
          <div class="input-wrap">
            <span class="input-icon">${IC.mail}</span>
            <input id="email" type="email" name="email" autocomplete="username" placeholder="voorbeeld@geovoice.nl"
              .value=${this.email} @input=${(e) => this.email = e.target.value} required ?disabled=${this.loading} />
          </div>
        </div>
        <div class="field">
          <label for="password">Wachtwoord</label>
          <div class="input-wrap">
            <span class="input-icon">${IC.lock}</span>
            <input id="password" type=${this._showPassword ? 'text' : 'password'} name="password" autocomplete="current-password" placeholder="••••••••"
              .value=${this.password} @input=${(e) => this.password = e.target.value} required ?disabled=${this.loading} />
            <button type="button" class="input-toggle" @click=${() => this._showPassword = !this._showPassword}
              aria-label=${this._showPassword ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}>${this._showPassword ? IC.eyeOff : IC.eye}</button>
          </div>
          <button type="button" class="link-forgot" @click=${() => { this.mode = 'forgot'; this.error = null; this.forgotSent = false; }}>Wachtwoord vergeten?</button>
        </div>
        <button type="submit" ?disabled=${this.loading}>${this.loading ? 'Bezig met inloggen…' : html`Inloggen ${IC.arrow}`}</button>
      </form>
      <p class="signup-hint">Nog geen account? <a href="mailto:info@mvib.nl">Neem contact op</a> om deze aan te vragen.</p>
    `;
  }

  render() {
    return html`
      <div class="login-layout" style="background-image:url(${loginBg})">
        <div class="login-scrim"></div>
        <header class="login-top">
          <img class="login-wordmark" src=${wordmarkUrl} alt="GeoVoice" />
        </header>
        <div class="login-body">
          <section class="login-hero">
            <h1 class="login-hero__title">Grip op bodemdata,<br />zonder je handen te gebruiken.</h1>
            <div class="login-hero__tagline">
              <span class="login-hero__leaf">${IC.leaf}</span>
              <span>Het complete platform voor bodemonderzoek, van veldwerk tot rapportage.</span>
            </div>
          </section>
          <section class="login-panel">
            <div class="login-card">${this._renderBody()}</div>
            <div class="login-footer">
              ${IC.shield}<span>Veilig en betrouwbaar</span><span class="dot">·</span><span>AVG-proof</span>
            </div>
          </section>
        </div>
      </div>
    `;
  }
}

customElements.define('login-form', LoginForm);
